// ============================================================================
//  Runtime capability probe (belt and suspenders).
//
//  Why this exists
//  ---------------
//  Our shipped ORT-Web runtime is the **SIMD + pthread** variant only
//  (`ort-wasm-simd-threaded.wasm`). It will refuse to load on a browser
//  that lacks WASM SIMD, and it will silently drop to a single thread on a
//  page that is not cross-origin isolated. Both failures look like "the
//  app is just inexplicably slow / broken" if you don't surface them.
//
//  We run a small set of checks at `createAsr()` time. SIMD is fatal
//  (we cannot recover); the rest are warnings.
//
//  Field reference
//  ---------------
//   simd                  WebAssembly.validate() on a v128.const module
//   sharedArrayBuffer     SAB constructor present in the global scope
//   crossOriginIsolated   globalThis.crossOriginIsolated === true. Requires
//                         the server send BOTH headers on every response:
//                           Cross-Origin-Opener-Policy: same-origin
//                           Cross-Origin-Embedder-Policy: require-corp
//                         Without these, SAB is unavailable in modern
//                         browsers → ORT silently runs single-threaded
//                         → ~3–4× slower WASM, no error message.
//                         Common offenders: default Cloudflare Pages /
//                         GitHub Pages / S3-without-policy.
//   webGpu                navigator.gpu present. On iPadOS 17+ Safari
//                         enables it by default; on Chrome it requires
//                         113+; on macOS Firefox it's behind a flag.
//   hardwareConcurrency   Used only to size `ort.env.wasm.numThreads`.
//                         Capped at 8 in backend.js because more threads
//                         net out slower for conformer-small (small ops,
//                         high pthread launch overhead).
// ============================================================================

// Canonical SIMD-detection wasm module (the one used by `wasm-feature-detect`
// and most Emscripten tooling): a single function returning `i8x16.popcnt(
// i8x16.splat(0))`. `WebAssembly.validate()` returns true iff the runtime
// understands v128 AND can decode at least one non-trivial SIMD opcode —
// some old engines accept the v128 type alone but balk on real SIMD ops.
const SIMD_TEST_MODULE = Uint8Array.of(
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
  0x01, 0x05, 0x01, 0x60, 0x00, 0x01, 0x7b,
  0x03, 0x02, 0x01, 0x00,
  0x0a, 0x0a, 0x01, 0x08, 0x00,
  0x41, 0x00, // i32.const 0
  0xfd, 0x0f, // i8x16.splat
  0xfd, 0x62, // i8x16.popcnt
  0x0b, // end
);

/** Memoized answer for wasmSimdSupported(); undefined until first probed. */
let _simdSupported;

/**
 * Canonical WASM-SIMD capability probe. Constant for the life of the browser
 * session. Shared by the ORT preflight below, the Vosk manifest resolver
 * (voskAssetManifest.js logs a loud failure if resolved without SIMD — there is
 * no scalar runtime anymore), and the local-ASR device gate
 * (localAsrEnablement.js, the layer that actually routes a non-SIMD device to
 * server Kaldi).
 *
 * Memoized because the result cannot change within a session and callers treat it
 * as free: explainLocalAsrEnablement() evaluates every gate eagerly on the stated
 * grounds that each is "a pure read of a flag, module state, the user record, or a
 * global", which an un-cached `WebAssembly.validate()` per call would not be.
 * @returns {boolean}
 */
export function wasmSimdSupported() {
  if (_simdSupported === undefined) {
    _simdSupported = safeValidate(SIMD_TEST_MODULE);
  }
  return _simdSupported;
}

/**
 * Drops the memoized SIMD answer so the next call re-probes. Test-only: the real
 * answer cannot change within a browser session, so nothing in the app should call
 * this. Exists because a test that swaps `WebAssembly.validate` without
 * `jest.resetModules()` would otherwise read the previous case's cached value and
 * pass or fail for the wrong reason.
 * @returns {void}
 */
export function resetSimdProbeForTests() {
  _simdSupported = undefined;
}

export function runtimeChecks() {
  return {
    simd: wasmSimdSupported(),
    sharedArrayBuffer: typeof globalThis.SharedArrayBuffer === "function",
    crossOriginIsolated: globalThis.crossOriginIsolated === true,
    webGpu: typeof navigator !== "undefined" && "gpu" in navigator,
    hardwareConcurrency: (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 1,
  };
}

export function assertRuntimeReady(checks) {
  if (!checks.simd) {
    throw new Error(
      "WASM SIMD not available, but the shipped runtime requires it. " +
      "Upgrade to Safari ≥16.4 / Chrome ≥91, or vendor a non-SIMD ORT-Web build.",
    );
  }
  if (!checks.crossOriginIsolated || !checks.sharedArrayBuffer) {
    // Non-fatal: ORT will fall back to numThreads=1 automatically. Still
    // worth screaming because the symptom is a silent 3–4× slowdown.
    // Diagnose the root cause first: in modern browsers SharedArrayBuffer
    // is gated on crossOriginIsolated, so a missing COOP/COEP pair forces
    // BOTH checks false — reporting "SAB not in scope" would mislead.
    const reason = !checks.crossOriginIsolated
      ? "page is not cross-origin isolated — set COOP/COEP headers"
      : "SharedArrayBuffer constructor not in scope";
    console.warn(
      `[asr] WASM threading DISABLED (${reason}). ` +
      "Send Cross-Origin-Opener-Policy: same-origin AND " +
      "Cross-Origin-Embedder-Policy: require-corp on every response. " +
      "Expect ~3–4× slower inference until fixed.",
    );
  }
}

function safeValidate(bytes) {
  try {
    return WebAssembly.validate(bytes);
  } catch {
    // Intentional swallow: the spec says validate() returns a boolean,
    // but old Safari/Edge throw on unknown opcodes instead. Either way
    // the answer is "this engine cannot run our SIMD wasm" → return
    // false and let assertRuntimeReady throw a user-friendly error.
    return false;
  }
}
